import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldAlert } from 'lucide-react';
import {
  Users, Activity, Clock, UserPlus, CheckCircle2, Timer,
  RefreshCw, Download, Sparkles, Plus, Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { useAuth } from '../../contexts/AuthContext';
import AdminCreateOperationModal from '../../components/AdminCreateOperationModal';
import { OpsKpiCard } from '../../components/ops-team/OpsKpiCard';
import { StatusDistribution } from '../../components/ops-team/StatusDistribution';
import { OperatorWorkloadTable } from '../../components/ops-team/OperatorWorkloadTable';
import { AssignmentQueue } from '../../components/ops-team/AssignmentQueue';
import { fmtNum } from '../../components/ops-team/opsMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIEW_ROLES = ['admin', 'super_admin', 'lead_ops', 'lead_field_ops'];

export default function OpsTeamWorkload() {
  const { user } = useAuth();
  const allowed = VIEW_ROLES.includes((user?.role || '').toLowerCase());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [autoBusy, setAutoBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const queueRef = useRef(null);

  const loadWorkload = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/ops/team/workload`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de charger la vue équipe Ops');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (allowed) loadWorkload(); else setLoading(false); }, [loadWorkload, allowed]);

  const refreshAll = useCallback(() => {
    loadWorkload();
    setRefreshKey((k) => k + 1);
  }, [loadWorkload]);

  const operators = (data?.operators || []).filter((o) => ['ops', 'field_ops'].includes(o.role));
  const t = data?.totals || {};
  const trends = data?.trends || {};

  const handleAutoAssign = async () => {
    if (!window.confirm('Assigner automatiquement toutes les opérations non assignées à l\'opérateur le moins chargé ?')) return;
    setAutoBusy(true);
    try {
      const res = await axios.post(`${API}/admin/ops/team/auto-assign`);
      toast.success(res.data?.message || 'Assignation automatique effectuée');
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'assignation automatique");
    } finally { setAutoBusy(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/ops/team/operations`, { params: { tab: 'all', page: 1, page_size: 100 } });
      const ids = (res.data?.operations || []).map((o) => o.id);
      const blobRes = await axios.post(`${API}/bulk-actions/export`, { operationIds: ids, format: 'csv' }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([blobRes.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ops_equipe_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export CSV généré');
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'export");
    } finally { setExporting(false); }
  };

  const selectStatus = (key) => {
    setStatusFilter(key);
    queueRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!allowed) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="ops-team-denied">
        <div className="max-w-md rounded-xl border border-amber-200 bg-amber-50/60 p-8 text-center shadow-sm">
          <ShieldAlert className="mx-auto h-10 w-10 text-amber-500" />
          <h2 className="mt-3 text-lg font-semibold text-gray-900">Accès réservé</h2>
          <p className="mt-1 text-sm text-gray-600">
            Le pilotage d'équipe est réservé aux rôles Lead Ops, Lead Field Ops et Admin.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ops-team-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">Pilotage des opérations</h1>
          <p className="mt-1 text-sm text-gray-500">Suivez la charge équipe, les retards et les opérations à assigner.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" className="h-9 text-xs" onClick={refreshAll} data-testid="ops-refresh-btn">
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Actualiser
          </Button>
          <Button variant="outline" size="sm" className="h-9 text-xs" onClick={handleExport} disabled={exporting} data-testid="ops-export-btn">
            {exporting ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />} Exporter
          </Button>
          <Button size="sm" className="h-9 bg-emerald-600 text-xs hover:bg-emerald-700" onClick={handleAutoAssign} disabled={autoBusy} data-testid="ops-auto-assign-btn">
            {autoBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />} Assigner automatiquement
          </Button>
          <Button size="sm" className="h-9 bg-gray-900 text-xs hover:bg-gray-800" onClick={() => setShowCreate(true)} data-testid="ops-create-op-btn">
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Créer une opération
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      {loading ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <OpsKpiCard icon={Users} label="Opérateurs actifs" value={fmtNum(t.operators)} tone="emerald" trend={trends.operators} testid="ops-kpi-operators" />
          <OpsKpiCard icon={Activity} label="Opérations actives" value={fmtNum(t.ongoing)} tone="teal" trend={trends.ongoing} testid="ops-kpi-ongoing" />
          <OpsKpiCard icon={Clock} label="Livraisons en retard" value={fmtNum(t.late)} tone="red" trend={trends.late} trendGood={false} testid="ops-kpi-late" />
          <OpsKpiCard icon={UserPlus} label="À assigner" value={fmtNum(t.unassigned)} tone="amber" trend={trends.unassigned} trendGood={false} testid="ops-kpi-unassigned" />
          <OpsKpiCard icon={CheckCircle2} label="Taux de livraison" value={`${t.deliveryRate ?? 0} %`} tone="emerald" trend={trends.deliveryRate} testid="ops-kpi-rate" />
          <OpsKpiCard icon={Timer} label="Délai moyen" value={t.avgDelayLabel || '—'} tone="blue" testid="ops-kpi-delay" />
        </div>
      )}

      {/* Distribution + charge */}
      {loading ? (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Skeleton className="h-80 rounded-xl" /><Skeleton className="h-80 rounded-xl" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <StatusDistribution funnel={data?.funnel || []} onSelectStatus={selectStatus} />
          <OperatorWorkloadTable operators={operators} onAssign={() => selectStatus('')} />
        </div>
      )}

      {/* File d'assignation */}
      <div ref={queueRef} className="scroll-mt-24">
        <AssignmentQueue
          operators={operators}
          status={statusFilter}
          onStatusChange={setStatusFilter}
          refreshKey={refreshKey}
          onMutated={loadWorkload}
        />
      </div>

      <AdminCreateOperationModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onSuccess={() => { setShowCreate(false); refreshAll(); }}
      />
    </div>
  );
}
